import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Radio, Calendar, Ticket, Video, Mic, Sparkles, Users } from 'lucide-react';
import { mediaUrl } from '../api/posts';
import { useAuth } from '../context/AuthContext';
import { buyLiveTicket } from '../api/lives';
import { useToast } from '../hooks/use-toast';
import { fmtCredits } from '../lib/currency';
import InsufficientBalanceDialog from './InsufficientBalanceDialog';
import { langFlag } from '../lib/creatorMeta';
import { useCategories } from '../api/categories';
import Avatar from './Avatar';

const fmtDateTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
};

/** Live card shown in the Home feed alongside posts. */
const LiveFeedCard = ({ live }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('lives');
  const { nameOf } = useCategories();
  const [loading, setLoading] = useState(false);
  const [insufficient, setInsufficient] = useState(false);
  const cover = live.cover_url ? mediaUrl(live.cover_url) : '';
  const isOwner = user && user.id === live.creator_id;
  const isLiveNow = live.status === 'live';
  const KindIcon = live.kind === 'audio' ? Mic : Video;

  const handleCta = async () => {
    // Free or already-paid OR live now → enter
    if (!live.is_paid || live.has_ticket || isOwner) {
      navigate(`/lives/${live.id}`);
      return;
    }
    // Need ticket → wallet-debit
    setLoading(true);
    try {
      await buyLiveTicket(live.id);
      toast({
        title: t('card.ticket_bought_title'),
        description: t('card.ticket_bought_desc', { amount: fmtCredits(live.price) }),
      });
      try { window.dispatchEvent(new CustomEvent('nymia:data-changed')); } catch (_) { /* noop */ }
      navigate(`/lives/${live.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 402 && detail.startsWith('insufficient_balance')) {
        setInsufficient(true);
      } else {
        toast({ title: t('card.purchase_failed_title'), description: detail.split('|')[1] || detail || '', variant: 'destructive' });
      }
      setLoading(false);
    }
  };

  const ctaLabel = !live.is_paid
    ? (isLiveNow ? t('feed_card.cta_enter_live') : t('feed_card.cta_set_reminder'))
    : (live.has_ticket || isOwner)
      ? t('feed_card.cta_enter_live')
      : t('feed_card.cta_buy_ticket', { price: fmtCredits(live.price) });

  return (
    <article className="mb-6 overflow-hidden border-b border-border bg-card md:rounded-lg md:border md:border-border">
      <header className="flex items-center justify-between px-3 py-2.5">
        <button onClick={() => navigate(`/u/${live.creator?.username}`)} className="flex items-center gap-3">
          <div className="rounded-full bg-gradient-to-tr from-amber-300 via-yellow-400 to-amber-600 p-[2px]">
            <div className="rounded-full bg-card p-[1.5px]">
              <Avatar src={live.creator?.avatar_url} name={live.creator?.username} size={32} />
            </div>
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold">{live.creator?.username}</p>
            <p className="text-xs text-muted-foreground">
              {isLiveNow ? t('feed_card.is_live_now') : t('feed_card.scheduled_a_live')}
            </p>
          </div>
        </button>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
            isLiveNow
              ? 'bg-red-500 text-white'
              : 'bg-amber-500/15 gold-text'
          }`}
        >
          {isLiveNow ? (
            <>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> {t('feed_card.live_now_badge')}
            </>
          ) : (
            <>
              <Calendar className="h-3 w-3" /> {t('feed_card.scheduled_badge')}
            </>
          )}
        </span>
      </header>

      <button onClick={handleCta} className="relative block aspect-square w-full overflow-hidden bg-black">
        {cover ? (
          <img src={cover} alt={live.title} className="h-full w-full object-cover" loading="lazy" decoding="async" />
        ) : (
          <div
            className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${
              isLiveNow
                ? 'from-red-700 via-rose-700 to-amber-900'
                : live.kind === 'audio'
                  ? 'from-amber-700 via-orange-700 to-rose-900'
                  : 'from-amber-900 via-neutral-800 to-black'
            }`}
          >
            <KindIcon className="h-24 w-24 text-white/40" strokeWidth={1.2} />
          </div>
        )}
        {/* Bottom gradient overlay */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-4 pt-12">
          <div className="flex items-center justify-between gap-3 text-left">
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-lg font-semibold text-white">{live.title}</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-white/80">
                {fmtDateTime(live.scheduled_at)} · {live.duration_minutes}m · {live.kind}
              </p>
              {isLiveNow && (
                <p className="mt-1 inline-flex items-center gap-1 text-xs text-amber-200">
                  <Users className="h-3 w-3" /> {t('feed_card.watching_now', { count: live.viewers_count })}
                </p>
              )}
            </div>
            {live.is_paid && (
              <div className="rounded-md bg-black/40 px-2 py-1 text-xs font-bold gold-text backdrop-blur">
                <Sparkles className="mr-1 inline h-3 w-3" />
                {fmtCredits(live.price)}
              </div>
            )}
          </div>
        </div>
      </button>

      <div className="flex items-center justify-between px-3 pt-3">
        <div className="text-xs text-muted-foreground">
          {t('feed_card.reserved', { count: live.tickets_sold })}
        </div>
        <button
          onClick={handleCta}
          disabled={loading}
          data-testid={isLiveNow ? 'live-card-join-now-btn' : 'live-card-book-btn'}
          className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold transition-all disabled:opacity-60 ${
            isLiveNow
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'gold-gradient hover:shadow-md hover:shadow-amber-900/30'
          }`}
        >
          {loading ? t('feed_card.cta_loading') : ctaLabel}
          {!loading && (live.is_paid && !live.has_ticket && !isOwner ? <Ticket className="h-4 w-4" /> : <Radio className="h-4 w-4" />)}
        </button>
      </div>
      {/* Phase 55 — creator info chips: categoria, gratuita/prezzo, lingue, argomenti */}
      <div className="flex flex-wrap gap-1.5 px-3 pb-3 pt-2" data-testid="live-card-info-chips">
        {live.category && (
          <span className="rounded-full bg-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-foreground">
            {nameOf(live.category) || live.category}
          </span>
        )}
        <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
          live.is_paid ? 'bg-amber-500/15 gold-text' : 'bg-emerald-500/15 text-emerald-600'
        }`}>
          {live.is_paid ? fmtCredits(live.price) : t('feed_card.free_badge')}
        </span>
        {(live.creator_languages || []).length > 0 && (
          <span className="rounded-full bg-secondary px-2.5 py-1 text-[10px] font-semibold">
            {live.creator_languages.map(langFlag).join(' ')}
          </span>
        )}
        {(live.creator_topics || []).map((topic) => (
          <span key={topic} className="rounded-full border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] font-semibold gold-text">
            {topic}
          </span>
        ))}
      </div>
      <InsufficientBalanceDialog
        open={insufficient}
        onOpenChange={setInsufficient}
        balance={Number(user?.wallet_balance || 0)}
        required={Number(live.price || 0)}
        context="ticket"
      />
    </article>
  );
};

export default React.memo(LiveFeedCard);
